/* The eager half of `/library/datasets/$name`.
 *
 * A route's `validateSearch` runs before its component is fetched, so anything it imports
 * is first-paint weight. A parser kept beside the screen it belongs to would pull the whole
 * screen into the entry chunk, because one module cannot be half-lazy. The vocabulary is
 * the part the router needs; the screen is not. */

import { enumParam } from "../../../lib/url-state";
import type { DatasetKind } from "../api";

type DatasetTab = "segments" | "lineage";

const TABS: readonly DatasetTab[] = ["segments", "lineage"];
const KINDS: readonly DatasetKind[] = ["demo", "correction", "online"];
// Two, not three: a recording is admitted to a dataset only after declaring which
// of these it is, so there is no "never declared" population to select.
const USES = ["training", "evaluation"] as const;

/** How one dataset is being read: which tab, and which kind of segment is shown. Neither
 *  says WHICH dataset or segment, so neither is an address. */
interface DatasetSearch {
  readonly tab?: DatasetTab;
  readonly kind?: DatasetKind;
  readonly use?: (typeof USES)[number];
}

/** Everything the dataset screen has selected: the two view keys above, plus the segment
 *  being inspected — which IS an address (`…/datasets/$name/segments/$segmentId`), and is
 *  therefore the one key the route puts in the path rather than in the query. */
export interface DatasetState extends DatasetSearch {
  readonly segment?: string;
}

export function parseDatasetSearch(
  search: Record<string, unknown>,
): DatasetSearch {
  return {
    tab: enumParam(search.tab, TABS),
    kind: enumParam(search.kind, KINDS),
    use: enumParam(search.use, USES),
  };
}
